import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { unwrapLookupItems } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton, FieldText, FieldSelect,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

/**
 * RiskMastersPage'in modal-edit'inin tam-sayfa karşılığı.
 * Alanlar: Kategori (lookup /api/riskcategories/Select), Kod (textId),
 * Tanım (name), Açıklama (description), Veri Tipi (dataType), Sıra (sortOrder).
 * Backend: /api/riskmasters Add/Update/Delete + /Select (GetById fallback).
 */
const API_BASE = '/api/riskmasters';
// Detay route tabanı: /risk-masters/new ve /risk-masters/:id
const DETAIL_BASE = '/risk-masters';
// İptal/kayıt sonrası dönülecek liste: embedded liste /link-risk-tracking sekmesinde.
const LIST_ROUTE = '/link-risk-tracking';

const DATA_TYPES = [
  { value: 1, label: 'Para' },
  { value: 2, label: 'Sayı' },
  { value: 3, label: 'Tarih' },
  { value: 4, label: 'Yüzde' },
  { value: 5, label: 'Evet/Hayır' },
];

const EMPTY_FORM = { categoryId: '', textId: '', name: '', description: '', dataType: 1, sortOrder: 0 };

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }
function categoryLabel(item) {
  if (!item) return '';
  return pick(item, 'description', 'Description') || pick(item, 'textId', 'TextId') || '';
}

export function RiskMasterDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Risk Tanım' : 'Risk Tanım Detayı', 'Kategori, tanım, açıklama ve veri tipi seçeneklerini yönetin.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // Kategori lookup
  useEffect(() => {
    fetch('/api/riskcategories/Select', { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setCategories(unwrapLookupItems(data)))
      .catch(() => {});
  }, []);

  const rowToForm = useCallback((r) => ({
    categoryId: pick(r, 'categoryId', 'CategoryId') ?? '',
    textId: pick(r, 'textId', 'TextId') ?? '',
    name: pick(r, 'name', 'Name') ?? '',
    description: pick(r, 'description', 'Description') ?? '',
    dataType: pick(r, 'dataType', 'DataType') ?? 1,
    sortOrder: pick(r, 'sortOrder', 'SortOrder') ?? 0,
  }), []);

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    if (location.state?.row) {
      setForm(rowToForm(location.state.row));
      // Nav-state satırı liste OData $select'inden gelir → audit alanları eksik.
      // Formu anında doldur; audit'i tam kayıttan (GetById) NON-BLOCKING çek.
      fetch(`${API_BASE}/GetById?Id=${id}`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => { const rec = unwrap(body); if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') }); })
        .catch(() => {});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let r = null;
      try {
        const res = await fetch(`${API_BASE}/GetById?Id=${id}`, { headers: authHeaders() });
        if (res.ok) r = unwrap(await res.json());
      } catch { /* GetById yoksa listeye düş */ }
      if (!r) {
        const res = await fetch(`${API_BASE}/Select`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`Kayıt alınamadı (${res.status})`);
        const items = unwrapLookupItems(await res.json());
        r = (Array.isArray(items) ? items : []).find((x) => String(pick(x, 'id', 'Id')) === String(id));
        if (!r) throw new Error('Kayıt bulunamadı');
      }
      setForm(rowToForm(r));
      setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Kayıt alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew, rowToForm]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const categoryOptions = categories.map((c) => ({ value: pick(c, 'id', 'Id'), label: categoryLabel(c) }));

  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.categoryId || !String(form.textId ?? '').trim() || !String(form.name ?? '').trim()) {
      showToast('error', 'Kategori, kod ve tanım zorunludur'); return;
    }
    setSaving(true);
    try {
      const payload = {
        categoryId: form.categoryId,
        textId: form.textId,
        name: form.name,
        description: form.description,
        dataType: Number(form.dataType),
        sortOrder: Number(form.sortOrder) || 0,
      };
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id, ...payload }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || (isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', 'Risk tanımı eklendi. Yeni kayda devam edebilirsiniz.');
          setForm({ ...EMPTY_FORM, categoryId: form.categoryId });
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Risk tanımı eklendi.');
        setTimeout(() => navigate(LIST_ROUTE), 400);
        return;
      }
      showToast('success', 'Risk tanımı güncellendi.');
      setTimeout(() => navigate(LIST_ROUTE), 400);
    } catch (err) {
      showToast('error', err.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();
  const handleClone = () => { navigate(`${DETAIL_BASE}/new`, { state: { clone: { ...form } } }); };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'Kayıt silinemedi');
      showToast('success', 'Risk tanımı silindi.');
      setTimeout(() => navigate(LIST_ROUTE), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate(LIST_ROUTE)} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni kayıt aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Risk Master Tanım', to: LIST_ROUTE }, { label: isNew ? 'Yeni' : 'Detay', current: true }]}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'RiskMaster', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="list" title="Risk Master Tanım" subtitle="Kategori, tanım, açıklama ve veri tipi">
          <Grid cols={2}>
            <FieldSelect
              form={form}
              set={set}
              tried={tried}
              k="categoryId"
              label="Kategori"
              req
              options={[{ value: '', label: 'Seçiniz' }, ...categoryOptions]}
            />
            <FieldSelect form={form} set={set} k="dataType" label="Veri Tipi" options={DATA_TYPES} />
            <FieldText form={form} set={set} tried={tried} k="textId" label="Kod" req mono />
            <FieldText form={form} set={set} k="sortOrder" label="Sıra" num />
            <FieldText form={form} set={set} tried={tried} k="name" label="Tanım" req span />
            <FieldText form={form} set={set} k="description" label="Açıklama" span />
          </Grid>
        </Section>
      </div>

      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.name || form.textId}”</strong> risk tanımını silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default RiskMasterDetailPage;
